import { BellRing, DatabaseZap, KeyRound, Mail, MoonStar, ShieldCheck, UserCog, Users } from "lucide-react";
import { NotificationPreferences } from "./NotificationPreferences";
import { MessageSendingSettings } from "./MessageSendingSettings";
import { AutomationSettingsPanel } from "./AutomationSettingsPanel";
import { type FormEvent, useEffect, useState } from "react";
import { Clock3 } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Avatar } from "../../components/Avatar";
import { InstallPrompt } from "../../components/InstallPrompt";
import { MfaEnrollment } from "../../components/MfaEnrollment";
import { PushControl } from "../../components/PushControl";
import { RichTextEditor } from "../../components/RichTextEditor";
import { TimezonePreference } from "../../components/TimezonePreference";
import { useSession } from "../../context/SessionContext";
import { useToast } from "../../context/ToastContext";
import { useTheme } from "../../context/ThemeContext";
import { useAsync } from "../../hooks/useAsync";
import { apiRequest } from "../../lib/api";
import { FreshdeskImportPanel, MailboxSettingsPanel, PeopleSettingsPanel, SecuritySettingsPanel } from "./AdminSettingsPanels";

type SettingsTab = "profile" | "users" | "mailboxes" | "security" | "import" | "automation";

const TABS: Array<{ id: SettingsTab; label: string; icon: typeof UserCog; admin?: boolean }> = [
  { id: "profile", label: "My preferences", icon: UserCog },
  { id: "users", label: "Staff & roles", icon: Users, admin: true },
  { id: "mailboxes", label: "Mailboxes", icon: Mail, admin: true },
  { id: "automation", label: "Automation & SLA", icon: Clock3, admin: true },
  { id: "security", label: "Security", icon: ShieldCheck, admin: true },
  { id: "import", label: "Freshdesk import", icon: DatabaseZap, admin: true },
];

function PasswordChangeForm({ forced = false, onComplete }: { forced?: boolean; onComplete?: () => void }) {
  const { changeStaffPassword } = useSession();
  const { showToast } = useToast();
  const [passwords, setPasswords] = useState({ current: "", next: "", confirm: "" });
  const [passwordBusy, setPasswordBusy] = useState(false);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  async function changePassword(event: FormEvent) {
    event.preventDefault();
    setPasswordError(null);
    if (passwords.next !== passwords.confirm) {
      const message = "New password confirmation does not match.";
      setPasswordError(message);
      showToast(message, "error");
      return;
    }
    setPasswordBusy(true);
    try {
      await changeStaffPassword(passwords.current, passwords.next);
      setPasswords({ current: "", next: "", confirm: "" });
      showToast("Password changed. Other staff sessions were revoked.", "success");
      onComplete?.();
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "Password could not be changed.";
      setPasswordError(message);
      showToast(message, "error");
    } finally { setPasswordBusy(false); }
  }

  return (
    <form className="inline-admin-form" onSubmit={changePassword}>
      {passwordError && <div className="form-alert" role="alert">{passwordError}</div>}
      <div className="form-grid">
        <label className="field"><span>{forced ? "Temporary password" : "Current password"}</span><input required type="password" autoComplete="current-password" value={passwords.current} onChange={(event) => setPasswords({ ...passwords, current: event.target.value })} autoFocus={forced} /></label>
        <label className="field"><span>New password</span><input required type="password" minLength={12} autoComplete="new-password" value={passwords.next} onChange={(event) => setPasswords({ ...passwords, next: event.target.value })} /><small>Use at least 12 characters and do not reuse the temporary password.</small></label>
        <label className="field"><span>Confirm new password</span><input required type="password" minLength={12} autoComplete="new-password" value={passwords.confirm} onChange={(event) => setPasswords({ ...passwords, confirm: event.target.value })} /></label>
      </div>
      <div className="form-actions"><button className="button button--primary" type="submit" disabled={passwordBusy}>{passwordBusy ? "Changing…" : forced ? "Set password and continue" : "Change password"}</button></div>
    </form>
  );
}

function ReplySignatureSettings() {
  const { showToast } = useToast();
  const result = useAsync((signal) => apiRequest<{ signatureHtml: string }>("/agent/profile/signature", { signal }), []);
  const [signatureHtml, setSignatureHtml] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (result.data) setSignatureHtml(result.data.signatureHtml); }, [result.data]);

  async function save(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      const response = await apiRequest<{ signatureHtml: string }>("/agent/profile/signature", { method: "PUT", body: { signatureHtml } });
      setSignatureHtml(response.signatureHtml);
      showToast("Reply signature saved.", "success");
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Signature could not be saved.", "error");
    } finally { setBusy(false); }
  }

  return (
    <section className="settings-section card">
      <header><div><h2>Reply signature</h2><p>Added automatically to the end of your public replies. Internal notes are never signed.</p></div></header>
      <form className="inline-admin-form signature-settings" onSubmit={save}>
        {result.error ? <div className="form-alert" role="alert">{result.error}</div> : <RichTextEditor html={signatureHtml} onChange={(nextHtml) => setSignatureHtml(nextHtml)} placeholder="Add your name, role, and contact details…" ariaLabel="Personal reply signature" disabled={busy || result.loading} />}
        <div className="form-actions"><button className="button button--primary" type="submit" disabled={busy || result.loading}>{busy ? "Saving…" : "Save signature"}</button>{signatureHtml && <button className="button button--secondary" type="button" disabled={busy} onClick={() => setSignatureHtml("")}>Clear</button>}</div>
      </form>
    </section>
  );
}

function PersonalSettings() {
  const { user } = useSession();
  const { preference, setPreference } = useTheme();

  return (
    <div className="settings-panel-stack">
      <section className="settings-section card"><header><div><h2>Staff profile</h2><p>Your display identity on internal notes and customer replies.</p></div></header><div className="staff-profile-settings"><Avatar name={user?.name ?? "Agent"} url={user?.avatarUrl} size="large" /><div><strong>{user?.name}</strong><span>{user?.email}</span>{user?.kind === "staff" && <small>{user.roles.join(" · ") || "Agent"}</small>}</div></div></section>
      <ReplySignatureSettings />
      <MessageSendingSettings />
      <NotificationPreferences />
      <section className="settings-section card"><header><div><h2>Timezone</h2><p>Dates and times in the agent workspace use this account setting. Staff default to UTC+7.</p></div><span className="source-pill"><Clock3 aria-hidden="true" size={16} /> Regional setting</span></header><div className="timezone-settings-body"><TimezonePreference /></div></section>
      <section className="settings-section card"><header><div><h2>Device preferences</h2><p>These settings apply to this browser only.</p></div></header><div className="preference-list"><article><span><BellRing aria-hidden="true" /></span><div><strong>Push notifications</strong><p>Receive assignments, customer replies, mentions, and SLA alerts.</p></div><PushControl /></article><article><span><MoonStar aria-hidden="true" /></span><div><strong>Appearance</strong><p>Match this workspace to your device or choose a fixed theme.</p></div><label className="compact-select"><span className="sr-only">Theme</span><select value={preference} onChange={(event) => setPreference(event.target.value as "light" | "dark" | "system")}><option value="system">System</option><option value="light">Light</option><option value="dark">Dark</option></select></label></article><article><span><DatabaseZap aria-hidden="true" /></span><div><strong>Add agent workspace to this device</strong><p>Save the responsive web app for quick access. Web Push is configured separately.</p></div><InstallPrompt compact /></article></div></section>
      <section className="settings-section card"><header><div><h2>Account security</h2><p>Multi-factor authentication is mandatory for all staff.</p></div></header><div className="security-summary"><span><KeyRound aria-hidden="true" /></span><div><strong>Authenticator app enabled</strong><p>This session was verified with MFA.</p></div><span className="status-text status-text--active"><i />Protected</span></div><PasswordChangeForm /></section>
    </div>
  );
}

export function AgentSettingsPage() {
  const { user } = useSession();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const enrollmentMode = user?.kind === "staff"
    && (user.mfaSetupRequired || params.get("enroll-mfa") === "1");
  const isAdmin = user?.kind === "staff" && (user.roles.some((role) => role.toLowerCase() === "admin") || user.permissions.includes("admin.access"));
  const requested = (params.get("tab") ?? "profile") as SettingsTab;
  const active: SettingsTab = TABS.some((tab) => tab.id === requested && (!tab.admin || isAdmin)) ? requested : "profile";

  function selectTab(tab: SettingsTab) {
    const next = new URLSearchParams(params);
    if (tab === "profile") next.delete("tab"); else next.set("tab", tab);
    setParams(next, { replace: true });
  }

  if (enrollmentMode) {
    return (
      <section className="agent-page page-stack settings-page">
        <header className="agent-page-header">
          <div>
            <p className="eyebrow">Account protection</p>
            <h1>Set up multi-factor authentication</h1>
            <p>Your setup session cannot access support data until the authenticator is verified.</p>
          </div>
        </header>
        <MfaEnrollment onComplete={() => navigate("/agent", { replace: true })} />
      </section>
    );
  }

  if (user?.kind === "staff" && user.passwordChangeRequired) {
    return (
      <section className="agent-page page-stack settings-page">
        <header className="agent-page-header">
          <div>
            <p className="eyebrow">Required security step</p>
            <h1>Replace your temporary password</h1>
            <p>Support data stays locked until you choose a private password. You cannot skip this step.</p>
          </div>
        </header>
        <section className="settings-section card">
          <header><div><h2>Choose your permanent password</h2><p>Enter the temporary password issued by your administrator, then create a new one.</p></div></header>
          <div className="settings-callout settings-callout--warning"><KeyRound aria-hidden="true" /><p><strong>Restricted session</strong>Only password change, MFA enrollment, session checks, and sign out are available until this is complete.</p></div>
          <PasswordChangeForm forced onComplete={() => navigate("/agent", { replace: true })} />
        </section>
      </section>
    );
  }

  return (
    <section className="agent-page page-stack settings-page">
      <header className="agent-page-header"><div><p className="eyebrow">Workspace configuration</p><h1>Settings</h1><p>{isAdmin ? "Manage support operations, security, and staff access." : "Manage your workspace preferences and device notifications."}</p></div></header>
      <div className="settings-layout">
        <nav className="settings-tabs" aria-label="Settings sections">{TABS.filter((tab) => !tab.admin || isAdmin).map(({ id, label, icon: Icon }) => <button key={id} type="button" className={active === id ? "is-active" : ""} onClick={() => selectTab(id)}><Icon aria-hidden="true" size={18} /><span>{label}</span></button>)}</nav>
        <div className="settings-content">
          {active === "profile" && <PersonalSettings />}
          {active === "users" && <PeopleSettingsPanel />}
          {active === "mailboxes" && <MailboxSettingsPanel />}
          {active === "automation" && <AutomationSettingsPanel />}
          {active === "security" && <SecuritySettingsPanel />}
          {active === "import" && <FreshdeskImportPanel />}
        </div>
      </div>
    </section>
  );
}
